import React, { useState } from 'react';
import { Table, Button, Row, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { generateNPInvoice, showNominalPayment } from '@app/api/nominalPayments.api';
import { generateLabInvoice, showLabTask } from '@app/api/labs.api';

interface Invoice {
  id: number;
  invoice_number: string;
  status: string;
  created_at: string;
}

interface PaymentsInfoProps {
  id: string | null;
  lab_code: string | null;
  invoice_data: Invoice[];
  labStatus?: string | null;
  wfActions: string[];
  invoicetype?: string | null;
  total?: string | number | null;
}

const PaymentsInfo: React.FC<PaymentsInfoProps> = ({
  id,
  lab_code,
  invoice_data,
  labStatus,
  wfActions,
  invoicetype,
  total,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<Invoice[]>([]);
  const tableData = invoiceData.length > 0 ? invoiceData : invoice_data ?? [];

  console.log('set total on tab:', total);

  const showInvoice = (
    invoiceId: number,
    status: string,
    invoice_number: string,
    labStatus: string,
    wfActions: string[],
  ) => {
    if (id !== null && invoicetype !== 'nominal_payment') {
      localStorage.setItem('isLab', 'true');
      navigate(`/view-invoice`, {
        state: { id, lab_code, invoiceId, status, invoice_number, labStatus, wfActions, total },
      });
    } else if (id !== null && invoicetype === 'nominal_payment') {
      navigate(`/view-nompay-invoice`, {
        state: { id, lab_code, invoiceId, status, invoice_number, labStatus, wfActions, total },
      });
    } else {
      notification.error({ message: 'Error', description: 'Invalid ID or Invoice' });
    }
  };

  const invoiceColumns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <span style={{ color: status === 'PAID' ? 'green' : 'red' }}>{status}</span>,
    },
    {
      title: 'Total (LKR)',
      dataIndex: 'total',
      key: 'total',
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: Invoice) => (
        <Row align="middle">
          <Button
            size="small"
            type="primary"
            onClick={() => showInvoice(record.id, record.status, record.invoice_number, labStatus ?? '', wfActions)}
          >
            View
          </Button>
        </Row>
      ),
    },
  ];

  const showGenerateButton = invoice_data.length === 0 && invoiceData.length === 0;

  const handleGenerateInvoice = async () => {
    if (!id) {
      notification.error({ message: 'Error', description: 'Invalid ID' });
      return;
    }
    if (invoicetype === 'nominal_payment') {
      try {
        setLoading(true);
        const result = await generateNPInvoice(Number(id));
        notification.success({
          message: 'Invoice Generated',
          description: `Invoice created successfully!`,
        });
        console.log('Generated Invoice:', result);
        const res = await showNominalPayment(Number(id));
        setInvoiceData(res?.nominal_payment?.invoices ?? []);
      } catch (err) {
        notification.error({
          message: 'Error',
          description: 'Failed to generate invoice',
        });
      } finally {
        setLoading(false);
      }
    } else if (invoicetype !== 'nominal_payment' && invoicetype !== null) {
      try {
        setLoading(true);
        const result = await generateLabInvoice(Number(id), 'LAB');
        notification.success({
          message: 'Invoice Generated',
          description: `Invoice created successfully!`,
        });
        console.log('Generated Invoice:', result);
        const res = await showLabTask(Number(id));
        setInvoiceData(res?.lab_request?.invoices ?? []);
      } catch (err) {
        notification.error({
          message: 'Error',
          description: 'Failed to generate invoice',
        });
      } finally {
        setLoading(false);
      }
    } else {
      notification.error({ message: 'Error', description: 'Invalid Invoice Type' });
    }
  };

  return (
    <>
      <h3>Invoices</h3>
      {showGenerateButton && (
        <div style={{ marginBottom: '8px' }}>
          <Button type="primary" size="small" loading={loading} onClick={() => handleGenerateInvoice()}>
            Generate Invoice
          </Button>
        </div>
      )}
      <Table dataSource={tableData} columns={invoiceColumns} rowKey="id" />

      <h3>Receipts</h3>
      <p>No receipts available yet.</p>
    </>
  );
};

export default PaymentsInfo;
